import { Project } from "src/redux/projects";
import * as State from "src/redux/projects/state";
import InstanceAxios from "src/plugins/axiosInstance";
import Moment from "moment";
import { config } from "src/config/config";
import { checkImage } from "src/helpers/checkImage";

/**
 *
 * @param statusId
 * Return prestation status
 */
function getPrestationStatus(statusId: number) {
  const PrestationStatus = [
    State.PrestationStatus.Draft,
    State.PrestationStatus.Modelisation,
    State.PrestationStatus.Graphics,
    State.PrestationStatus.Changing,
    State.PrestationStatus.WaitPov,
    State.PrestationStatus.WaitValidClient,
    State.PrestationStatus.Valid,
    State.PrestationStatus.WaitValidInternal,
    State.PrestationStatus.Delivery
  ];
  return statusId >= 0 && statusId < PrestationStatus.length
    ? PrestationStatus[statusId]
    : State.PrestationStatus.Draft;
}

function actionStatus(date) {
  const dayDate = Moment();
  const daysDiff = date.diff(dayDate, "days");
  if (daysDiff < 2) {
    return State.PrestationActionStatus.Hight;
  }
  if (daysDiff < 5) {
    return State.PrestationActionStatus.Medium;
  }
  return State.PrestationActionStatus.Low;
}

/***
 *  Login
 ***/

export async function setLogin(Userlogin, Userpwd) {
  const response = await InstanceAxios.post("/login", {
    login: Userlogin,
    pwd: Userpwd
  });
  if (response.data.token) {
    localStorage.setItem("authToken", response.data.token);
  }
  return response.data;
}
export async function shareProposal(mail, proposalId) {
  const response = await InstanceAxios.post(
    "/client/proposal/" + proposalId + "/share",
    {
      to: mail.to,
      message: mail.msg
    }
  );

  return response.data;
}
/**
 * Fetch proposal pending
 */
export function fetchProposalPendingByProjectId(projectId, response) {
  if (!response) {
    return [];
  }
  const filterPrestationAction = response.data.filter(
    a => a.projetId === projectId
  );
  const actions = filterPrestationAction.map(p => toApiPrestationAction(p));
  return actions;
}

/**
 * Fetch project using it's id
 */
export async function fetchProjectById(projectId: number): Promise<Project> {
  const response = await InstanceAxios.get(`/client/projets/${projectId}`);
  const responseProposalsPending = await InstanceAxios.get(
    `client/proposals/pending`
  );
  const proposalsPending = fetchProposalPendingByProjectId(
    projectId,
    responseProposalsPending
  );

  const status =
    response.data.StatutProjet.id === 1
      ? State.StatusProject.Deliver
      : State.StatusProject.InProgress;

  const project: State.Project = await toApiProject(
    response.data,
    status,
    proposalsPending
  );
  return project;
}

/**
 * Fetch project corresponding to a given status
 * By default, status is "in progress"
 */
export async function fetchProjects(
  status?: State.StatusProject
): Promise<Project[]> {
  let urlProject = "/client/projets";
  if (status) {
    const statusNumeric = status === State.StatusProject.InProgress ? 0 : 1;
    urlProject += `?status=${statusNumeric}`;
  }
  // return all projects in progress
  const response = await InstanceAxios.get(urlProject);
  const responseProposalsPending = await InstanceAxios.get(
    `client/proposals/pending`
  );
  const projects = await Promise.all<Project>(
    response.data.map(p =>
      toApiProject(
        p,
        p.StatutProjet.id === 1
          ? State.StatusProject.Deliver
          : State.StatusProject.InProgress,
        fetchProposalPendingByProjectId(p.id, responseProposalsPending)
      )
    )
  );
  // Projects are ordered by name
  return projects.sort((a, b) => {
    if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    }
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    }
    return 0;
  });
}

/**
 * Fetch a given prestation from a given project
 */
export async function fetchPrestationById(
  projectId: number,
  prestationId: number
): Promise<State.Prestation> {
  const response = await InstanceAxios.get(
    `/client/projets/${projectId}/prestations/${prestationId}`
  );

  return {
    id: response.data.id,
    name: response.data.nom,
    status: getPrestationStatus(+response.data.statut_prestation_id),
    dueDate: Moment(response.data.date),
    image: response.data.thumbUrl ? response.data.thumbUrl : "",
    proposals: [],
    type_prestation: response.data.type_prestation,
    isLoadingProposals: false
  };
}
export async function fetchPrestations(
  projectId: number
): Promise<State.Prestation[]> {
  const response = await InstanceAxios.get(
    `/client/projets/${projectId}/prestations/`
  );
  const prestations = response.data.map(p => {
    return {
      id: p.id,
      name: p.nom,
      status: getPrestationStatus(+p.statut_prestation_id),
      dueDate: Moment(p.date),
      image: p.thumbUrl ? p.thumbUrl : "",
      imageDelivery: p.thumbUrl ? p.thumbUrl.replace("vignettes/", "") : "",
      url: p.url,
      delivery: p.livraison_illustration_id
        ? `${config.downloadUrl}drive/telecharger?illustr=${p.livraison_illustration_id}&id=${p.projet_id}`
        : undefined,
      deliveryId: p.livraison_illustration_id
        ? p.livraison_illustration_id
        : undefined,
      proposals: [],
      type_prestation: p.type_prestation,
      isLoadingProposals: false
    };
  });
  return prestations;
}
/**
 * Format all prestations of a given project
 */
function formatPrestations(prestationsData): State.Prestation[] {
  const prestations: State.Prestation[] = prestationsData.map(p => ({
    id: p.id,
    name: p.nom,
    status: getPrestationStatus(+p.statut_prestation_id),
    dueDate: Moment.utc(p.date),
    image: p.thumbUrl ? p.thumbUrl : "",
    url: p.url ? p.url : "",
    imageDelivery: p.thumbUrl ? p.thumbUrl.replace("vignettes/", "") : "",
    delivery: p.livraison_illustration_id
      ? `${config.downloadUrl}drive/telecharger?illustr=${p.livraison_illustration_id}&id=${p.projet_id}`
      : undefined,
    deliveryId: p.livraison_illustration_id
      ? p.livraison_illustration_id
      : undefined,
    proposals: [],
    type_prestation: p.type_prestation,
    isLoadingProposals: false
  }));

  // Prestations are ordered by due date
  return prestations.sort((a, b) => b.dueDate.diff(a.dueDate, "second"));
}

function getProposalType(type) {
  switch (type) {
    case "illustr":
      return State.ProposalType.Artwork;
    case "angle":
      return State.ProposalType.POV;
    case "deco":
      return State.ProposalType.PlanDecoration;
    default:
      return;
  }
}
function getProposalStatus(status) {
  switch (status) {
    case -1:
      return State.ProposalStatus.Rejected;
    case 0:
      return State.ProposalStatus.Pending;
    default:
      return State.ProposalStatus.Validated;
  }
}
function gtResponseStatus(status) {
  switch (status) {
    case -1:
      return State.ResponseTypes.Rejected;
    case 0:
      return State.ResponseTypes.Pending;
    default:
      return State.ResponseTypes.Validated;
  }
}

async function getProposalRessources(proposal, type) {
  const ressources = [] as State.Ressource[];
  switch (type) {
    case "illustr":
      ressources.push({
        id: 0,
        label: proposal.titre1,
        image: proposal.url,
        thumb: proposal.thumbUrl
      });
      return ressources;
    case "angle":
      // There is always at last one ressource
      const thumbUrl1 = proposal.thumbUrl1;
      const imageUrl1 = proposal.url1;

      ressources.push({
        id: 0,
        label: proposal.titre1,
        image: imageUrl1,
        thumb: thumbUrl1
      });

      // ressource 2 might be absent
      try {
        const thumbUrl2 = proposal.thumbUrl2;
        const imageUrl2 = proposal.url2;
        await checkImage(imageUrl2);
        ressources.push({
          id: 1,
          label: proposal.titre2,
          image: imageUrl2,
          thumb: thumbUrl2
        });
      } catch (e) {
        console.warn("No ressource for POV 2. POV ignored");
      }

      // ressource 3 might be absent
      try {
        const thumbUrl3 = proposal.thumbUrl3;
        const imageUrl3 = proposal.url3;

        await checkImage(imageUrl3);

        ressources.push({
          id: 2,
          label: proposal.titre3,
          image: imageUrl3,
          thumb: thumbUrl3
        });
      } catch (e) {
        console.warn("No ressource for POV 3. POV ignored");
      }

      return ressources;
    case "deco":
      if (!proposal.choices) {
        return;
      }
      const choices = proposal.choices.map(c => ({
        id: c.id,
        label: c.nom,
        image: c.image,
        payload: {
          color: c.color
        },
        thumb: c.thumb
      }));
      return choices;
    default:
      return ressources;
  }
}
export async function fetchProposalByUuid(
  proposalUuid
): Promise<State.Proposal> {
  const response = await InstanceAxios.get("/client/proposals/" + proposalUuid);
  return toProposal(response.data);
}

export async function submitCurrentResponse(
  response: State.GenericalResponse,
  proposal: State.Proposal
) {
  // Convert our response structure to a format that api will understand
  let formattedResponse;
  switch (proposal.type) {
    case State.ProposalType.Artwork:
      formattedResponse = toApiArtwork(response as State.ResponseArtwork);
      break;

    case State.ProposalType.POV:
      formattedResponse = toApiPOV(response as State.ResponsePov, proposal);
      break;

    case State.ProposalType.PlanDecoration:
      formattedResponse = toApiDecoration(
        response as State.ResponsePlanDecoration
      );
      break;

    default:
      console.error("Unknown proposal type");
      return;
  }

  await InstanceAxios.put(
    "/client/proposals/" + proposal.uuid,
    formattedResponse
  );
}

/**
 * Retrieve all proposals of a given prestation
 */
export async function fetchPrestationProposals(
  projectId: number,
  prestationId: number
): Promise<State.Proposal[]> {
  // Load all proposals history
  const respProposalList = await InstanceAxios.get(
    `/client/projets/${projectId}/prestations/${prestationId}/proposals`
  );

  // Also load all proposal details, so we can have user comments ...
  // FIXME: Some proposal do not have an uuid, we have to filter them
  const proposals = await Promise.all<State.Proposal>(
    respProposalList.data
      .filter(d => d.uuid !== "")
      .map(d => toProposalWithoutResponses(d))
  );
  // Order by creation date
  return proposals.sort((a, b) => a.date.diff(b.date, "second"));
}

/**
 * Format api data to one of our proposal format
 */
async function toProposalWithoutResponses(data) {
  // Common informations
  const common: State.Proposal = {
    id: data.id,
    uuid: data.uuid,
    date: data.date_validation
      ? Moment(data.date_validation)
      : Moment(data.date_crea),
    type: getProposalType(data.type) as any,
    technician: data.technicien ? data.technicien : "",
    status: getProposalStatus(data.statut),
    session: data.session,
    ressources: await getProposalRessources(data, data.type),
    responses: []
  };

  return common;
}

/**
 * Format api data to one of our proposal format
 */
async function toProposal(data) {
  // Common informations
  const common: State.Proposal = {
    id: data.id,
    uuid: data.uuid,
    date: data.date_validation
      ? Moment(data.date_validation)
      : Moment(data.date_crea),
    type: getProposalType(data.type) as any,
    technician: data.technicien ? data.technicien : "",
    status: getProposalStatus(data.statut),
    session: data.session,
    ressources: await getProposalRessources(data, data.type),
    responses: []
  };

  // Artwork proposal have comments, contained at validNotes key
  if (data.ValidNotes) {
    const comments: State.ProposalComment[] = data.ValidNotes.map(d => ({
      id: d.id.toString(),
      x: d.repere_x,
      y: d.repere_y,
      text: d.note,
      files: d.Files.map(f => ({
        id: f.id,
        filename: f.filename,
        url: f.url
      }))
    }));

    const payload: State.PayloadArtwork = {
      comments,
      comment: data.note,
      files: data.Files.map(f => ({
        id: f.id,
        filename: f.filename,
        url: f.url
      })),
      // Those are not in api response for now
      askedEveryone: "",
      summarizedEverything: ""
    };

    // For now, api only send one response
    const response: State.ResponseArtwork = {
      // we use proposal id as response id, as they're is only one response by proposal
      id: data.uuid,
      // does not exist in api response
      ressourceId: 0,
      authorId: data.auteur_id,
      date: common.date,
      // We use proposal status for now
      responseType: gtResponseStatus(data.statut),
      payload
    };
    (common as State.ProposalArtwork).responses = [response];
    if(data.rank!==undefined){
      (common as State.ProposalArtwork).rank=data.rank;
    }
  }
  // POV proposal have generic comments
  if (data.type === "angle") {
    const response: State.ResponsePov = {
      // we use proposal id as response id, as they're is only one response by proposal
      id: data.uuid,
      // does not exist in api response
      ressourceId: data.statut > 0 ? data.statut - 1 : 0,
      authorId: data.auteur_id,
      date: common.date,
      // We use proposal status for now
      responseType: gtResponseStatus(data.statut),
      payload: {
        comment: data.commentaire_client,
        files: data.Files.map(f => ({
          id: f.id,
          filename: f.filename,
          url: f.url
        }))
      }
    };
    (common as State.ProposalPOV).responses = [response];
  }
  // Deco
  if (data.type === "deco") {
    const payload: State.PayloadPlanDecoration = {
      comment: data.note,
      // Those are not in api response for now
      cgv: false,
      lastVersionTransmitted: false
    };
    const response: State.ResponsePlanDecoration = {
      // we use proposal id as response id, as they're is only one response by proposal
      id: data.uuid,
      ressourceId: data.decoration_id,
      authorId: data.auteur_id,
      date: common.date,
      // We use proposal status for now
      responseType: gtResponseStatus(data.statut),
      payload
    };
    (common as State.ProposalPlanDecoration).responses = [response];
  }

  return common;
}

/**
 * Convert our POV response structure to a json that api expects
 */
function toApiPOV(response: State.ResponsePov, proposal: State.ProposalPOV) {
  return {
    type: "angle",
    auteur_id: response.authorId,
    files: response.payload ? response.payload.files : [],
    commentaire_client: response.payload ? response.payload.comment : "",
    statut:
      response.responseType === State.ResponseTypes.Rejected
        ? -1
        : proposal.ressources.findIndex(r => r.id === response.ressourceId) + 1 // A statut > 0 means proposal is accepted
  };
}

/**
 * Convert our Artwork response structure to a format that api expects
 */
function toApiArtwork(response: State.ResponseArtwork) {
  return {
    type: "illustr",
    ValidNotes: response.payload.comments.map(c => ({
      id: /^tmp-/.test(c.id) ? undefined : +c.id,
      repere_x: c.x,
      repere_y: c.y,
      note: c.text,
      files: c.files
    })),
    files: response.payload.files,
    note: response.payload.comment || "",
    askedEveryone: response.payload.askedEveryone,
    summarizedEverything: response.payload.summarizedEverything,
    auteur_id: response.authorId,
    statut:
      response.responseType === State.ResponseTypes.Validated
        ? 1
        : response.responseType === State.ResponseTypes.Rejected
        ? -1
        : 0
  };
}

/**
 * Convert our Decoration response structure to a format api expects
 */
function toApiDecoration(response: State.ResponsePlanDecoration) {
  return {
    type: "deco",
    auteur_id: response.authorId,
    decoration_id: response.ressourceId,
    statut: response.responseType === State.ResponseTypes.Pending ? 0 : 1, // Decoration can only be accepted,
    note: response.payload ? response.payload.comment : "",
    cgv: response.payload ? response.payload.cgv : false,
    lastVersionTransmitted: response.payload
      ? response.payload.lastVersionTransmitted
      : false
  };
}

function toApiPrestationAction(response) {
  return {
    uuid: response.uuid,
    nom: response.nom,
    thumbUrl: response.thumbUrl,
    prestation_id: response.prestation_id,
    type_prestation: response.type_prestation,
    type: getProposalType(response.type),
    proposalStatus: getProposalStatus(response.statut),
    status: actionStatus(Moment(response.date_crea)),
    date_crea: Moment(response.date_crea),
    auteur: response.auteur && response.auteur.complete_name
  };
}
async function toApiProject(response, status, proposalsPending) {
  return {
    id: response.id,
    refArka: response.ref + "-" + response.exercice,
    name: response.nom,
    city: response.ProjetArchi && response.ProjetArchi.ville,
    prestationCount: response.prestationCount,
    status,
    image: response.thumbUrl ? response.thumbUrl : "",
    prestationsAction: proposalsPending,
    projectStats: response.stats,
    prestations: response.prestations
      ? formatPrestations(response.prestations)
      : [],
    isLoadingPrestations: false,
    financialYear: response.exercice,
    projectManager: response.manager
      ? {
          id: response.id,
          firstname: response.manager.nom,
          lastname: response.manager.prenom,
          phone: response.manager.tel1 || response.manager.tel2,
          email: response.manager.mail1 || ""
        }
      : undefined
  };
}

/**
 * setUploadFiles
 * upload api route
 */

export async function setUploadFiles(file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await InstanceAxios.post("/client/uploads", formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
  return response.data;
}



// WEBPACK FOOTER //
// ./src/api/projects.tsx